export type EnvironmentId = 'localhost' | 'eilat' | 'staging01' | 'production';

export type EnvironmentConfig = {
  id: EnvironmentId;
  label: string;
  payoutApiHost: string;
  portalHost: string;
  live?: boolean;
};

/**
 * Local-testing override for the staging01 payouts (`/v2`) host. Set `VITE_STAGING01_PAYOUT_API_HOST`
 * (in `apps/web/config/.env.development.local`) to point the browser's staging01 `/v2` calls — the
 * onboarding entity-create/list and the API playground — at a partnerships-api running on your
 * machine that itself connects to the staging01 DB (partnerships-api's "local vs staging01" mode).
 * The env identity stays `staging01`, so the partner/data provisioned in the staging01 DB line up.
 * Unset in every real build, so deployed behaviour is unchanged.
 */
const STAGING01_PAYOUT_API_HOST = import.meta.env.VITE_STAGING01_PAYOUT_API_HOST || 'https://api.staging01.melio.com';

export const ENVIRONMENTS: Record<EnvironmentId, EnvironmentConfig> = {
  localhost: {
    id: 'localhost',
    label: 'Local',
    payoutApiHost: 'http://localhost:3000',
    portalHost: 'http://localhost:3001',
  },
  eilat: {
    id: 'eilat',
    label: 'Eilat (edge)',
    payoutApiHost: 'https://partnerships-api.eilat.melioservices.com',
    portalHost: 'https://developers-portal-api.eilat.melioservices.com',
  },
  staging01: {
    id: 'staging01',
    label: 'staging01',
    payoutApiHost: STAGING01_PAYOUT_API_HOST,
    portalHost: 'https://developers-portal-api.staging01.melioservices.com',
  },
  production: {
    id: 'production',
    label: 'Production',
    payoutApiHost: 'https://api.melio.com',
    portalHost: 'https://developers-portal-api.melio.com',
    live: true,
  },
};

export const ENVIRONMENT_IDS = Object.keys(ENVIRONMENTS) as EnvironmentId[];

export const isEnvironmentId = (value: unknown): value is EnvironmentId => typeof value === 'string' && value in ENVIRONMENTS;

/**
 * Environments the signed-in dashboard operates against — the dashboard env switcher's options. Local
 * dev narrows this to `staging01` (the only env a local build can safely reach — production's payouts
 * `/v2` calls would hit real api.melio.com) via `VITE_DASHBOARD_ENVS`, mirroring {@link ONBOARDING_ENVS}.
 */
export const DASHBOARD_ENV_IDS: EnvironmentId[] = (import.meta.env.VITE_DASHBOARD_ENVS || 'staging01,production')
  .split(',')
  .map((value: string) => value.trim())
  .filter((value: string): value is EnvironmentId => isEnvironmentId(value));

export const defaultEnvironmentForHost = (hostname: string): EnvironmentId => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
  if (hostname.includes('staging01')) return 'staging01';
  if (hostname.includes('eilat')) return 'eilat';
  if (hostname.includes('melio.com')) return 'production';
  return 'eilat';
};

export const defaultEnvironment = (stackEnv: string | undefined, hostname: string): EnvironmentId => {
  if (isEnvironmentId(stackEnv)) return stackEnv;
  if (stackEnv === 'development') return 'localhost';
  return defaultEnvironmentForHost(hostname);
};

export const LOCAL_ADMIN_TOKEN = 'local-payouts-admin-token';

export const apiBaseUrl = (env: EnvironmentId): string => `${ENVIRONMENTS[env].payoutApiHost}/v2`;

export const portalBaseUrl = (env: EnvironmentId): string => ENVIRONMENTS[env].portalHost;

/**
 * Base to prefix before a portal API path (`/me`, `/onboard`, `/keys`) for an env. The developers-portal
 * API is **single-env** — each env (staging01, production) has its own deployment — so the browser
 * addresses each env's onboarding/keys behind that env's own portal API:
 *   • production (where the SPA is served) is reached same-origin under `/api`, which CloudFront strips
 *     before the request hits the API — no CORS;
 *   • other envs are reached at their portal host directly (that API allows the SPA origin via CORS).
 * `VITE_<ENV>_PORTAL_API_BASE` overrides either, so local dev points both at the one local API's `/api`
 * proxy (avoids https→http mixed-content that a direct localhost call would hit).
 */
export const portalApiBase = (env: EnvironmentId): string => {
  const override =
    env === 'production'
      ? import.meta.env.VITE_PRODUCTION_PORTAL_API_BASE
      : env === 'staging01'
        ? import.meta.env.VITE_STAGING01_PORTAL_API_BASE
        : undefined;
  return override || (env === 'production' ? '/api' : ENVIRONMENTS[env].portalHost);
};

/**
 * The envs onboarding provisions the partner into — one browser call per env, each to that env's portal
 * API (no server-side cross-env fan-out). Local dev narrows this to `staging01` (the one reachable env)
 * via `VITE_ONBOARDING_ENVS`.
 */
export const ONBOARDING_ENVS: EnvironmentId[] = (import.meta.env.VITE_ONBOARDING_ENVS || 'staging01,production')
  .split(',')
  .map((value: string) => value.trim())
  .filter((value: string): value is EnvironmentId => isEnvironmentId(value));

/**
 * The env whose `/me` answers the onboarding gate — production by product decision. When production is
 * excluded from the dashboard (local dev, via `VITE_DASHBOARD_ENVS`) the gate falls back to the first
 * reachable dashboard env (staging01), so a local build never calls production on boot.
 */
export const GATE_ENV: EnvironmentId = DASHBOARD_ENV_IDS.includes('production') ? 'production' : (DASHBOARD_ENV_IDS[0] ?? 'staging01');

const TBT_KEY_PROD = 'key_VygUcaXQL4NtSUMxpKxjS';
const TBT_KEY_TEST = 'key_GZFc6KNn6zbN6Y54aFZfpV';

export const tbtConfig = (env: EnvironmentId): { apiKey: string; environment: 'test' | 'production' } => ({
  apiKey: env === 'production' ? TBT_KEY_PROD : TBT_KEY_TEST,
  environment: env === 'production' ? 'production' : 'test',
});

export type SonarSdkConfig = {
  scriptUrl: string;
};

const EILAT_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.eilat.melio.com/v1/sonar-sdk.js' };
const STAGING01_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.staging01.melio.com/v1/sonar-sdk.js' };
const PRODUCTION_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.melio.com/v1/sonar-sdk.js' };

const SONAR_SDK_BY_ENV: Partial<Record<EnvironmentId, SonarSdkConfig>> = {
  localhost: STAGING01_SONAR,
  eilat: EILAT_SONAR,
  staging01: STAGING01_SONAR,
  production: PRODUCTION_SONAR,
};

export const sonarSdkConfig = (env: EnvironmentId): SonarSdkConfig | null => SONAR_SDK_BY_ENV[env] ?? null;
